import { Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ApiRoutes } from '@lsq-frontend/angular-shared/app-constants';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import {
    GeneratePresignedUrlForBuyerAccountRequest,
    GeneratePresignedUrlForBuyerAccountService,
    GeneratePresignedUrlForBuyerRequest,
    GeneratePresignedUrlForBuyerService,
    GetBuyerAccountUploadTemplateRequest,
    GetBuyerAccountUploadTemplateService,
    GetBuyerUploadTemplateService,
    ImportS3ForBuyerAccountOnlyRequest,
    ImportS3ForBuyerAccountOnlyService,
    ImportS3ForBuyerOnlyRequest,
    ImportS3ForBuyerOnlyService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    DashboardClaim,
    FileType,
    ResponseCode
} from '@lsq-frontend/remote-services/enterprise/common';
import { Color, ContentTypes, DownloadService, forLifeOf } from '@lsq-frontend/common-ui';
import { ClaimService, Icons } from '@lsq-frontend/common-ui';
import { ModalWorkflowService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FileUpload, HttpMethod, Uploader } from 'rxjs-uploader';
import { first, map, shareReplay } from 'rxjs/operators';

@Component({
    selector: 'buyer-account-upload-modal',
    templateUrl: './buyer-account-upload-modal.component.html',
    styleUrls: ['./buyer-account-upload-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BuyerAccountUploadModalComponent {
    @ViewChild('uploadButton') public uploadButtonRef!: ElementRef;

    public icons = Icons;
    public colors = Color;
    public hasFileSelected = false;
    public fileUpload$?: Observable<FileUpload>;
    public uploadButtonClickSubject = new Subject();
    public uploadErrorMessage?: string | null;
    public fileUploader: Uploader = new Uploader<FileUpload>({
        allowedContentTypes: ContentTypes.Csvs,
        requestOptions: {
            url: '',
            headers: {
                'Content-Type': 'text/csv'
            }
        },
        fileSizeLimitMb: 100,
        fileCountLimit: 1,
        requestUrl: '',
        uploadFileAsBody: true,
        disallowedContentTypeErrorMessage: () => this.uploadWrongFileTypeErrorMessageI18N,
        disallowedContentSizeErrorMessage: () => this.contentSizeErrorMessageI18N,
        fileUploadedCallback: () => {
            if (this._claimService.isOnSync(DashboardClaim.FinancingAccountPortal)) {
                this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
                    `/${ApiRoutes.Api}/ImportS3ForBuyerAccountOnly`,
                    ResponseCode.Error
                );
                try {
                    // tslint:disable-next-line: no-floating-promises
                    this._importS3ForBuyerAccountOnlyService.executeOnce(
                        new ImportS3ForBuyerAccountOnlyRequest()
                    );
                } catch (error) {
                    // do nothing
                }
                this._modalWorkflowService.nextStep();
            } else {
                this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
                    `/${ApiRoutes.Api}/ImportS3ForBuyerOnly`,
                    ResponseCode.Error
                );
                try {
                    // tslint:disable-next-line: no-floating-promises
                    this._importS3ForBuyerOnlyService.executeOnce(
                        new ImportS3ForBuyerOnlyRequest()
                    );
                } catch (error) {
                    // do nothing
                }
                this._modalWorkflowService.nextStep();
            }
        },
        allFilesQueuedCallback: async (fileUploads) => {
            await this.uploadButtonClickSubject.pipe(first()).toPromise();
            this.fileUploader.patchRequestOptions({
                method: HttpMethod.Put
            });
            const presignedUrl = await this._getPresignedUrl();
            const uri = presignedUrl.split('?')[0].split('/');
            const fileName = uri
                .slice(Math.max(uri.length - 4, 0))
                .join('/')
                .trim();
            const newFile = this._renameFile(fileUploads, fileName);
            this.fileUploader.setRequestUrl(presignedUrl);
            return [new FileUpload(newFile)];
        }
    });

    public fileName$ = new BehaviorSubject('');
    public buttonText$ = this.fileName$.pipe(
        map((fileName) => this._i18n(`Attach ${fileName} CSV`)),
        shareReplay(1)
    );
    public getUploadTemplateRequest$ = new BehaviorSubject(
        new GetBuyerAccountUploadTemplateRequest()
    );
    private getPresignedUrlRequest$ = new BehaviorSubject(
        new GeneratePresignedUrlForBuyerAccountRequest()
    );

    private readonly uploadWrongFileTypeErrorMessageI18N = this._i18n(
        'Your file upload must be in CSV format.'
    );
    private readonly contentSizeErrorMessageI18N = this._i18n('Your file exceeds the 100mb limit.');
    private readonly genericErrorMessageI18N = this._i18n(
        'The upload was not successful, please try again and if the problem persists contact support at '
    );
    private readonly _fastTrackEmailAddress = 'fasttrack-support@lsq.com';

    private readonly _errorMessage = `${this.genericErrorMessageI18N}<a href="mailto:${this._fastTrackEmailAddress}" class="mail-to preserve-case">${this._fastTrackEmailAddress}</a>`;

    constructor(
        protected _getBuyerAccountUploadTemplateService: GetBuyerAccountUploadTemplateService,
        protected _getBuyerUploadTemplateService: GetBuyerUploadTemplateService,
        protected _generatePresignedUrlService: GeneratePresignedUrlForBuyerAccountService,
        protected _generatePresignedBuyerUrlService: GeneratePresignedUrlForBuyerService,
        protected _importS3ForBuyerAccountOnlyService: ImportS3ForBuyerAccountOnlyService,
        protected _importS3ForBuyerOnlyService: ImportS3ForBuyerOnlyService,
        protected _downloadService: DownloadService,
        protected _modalWorkflowService: ModalWorkflowService,
        protected _errorToastBlacklistService: ErrorToastBlacklistService,
        protected _claimService: ClaimService,
        protected _i18n: I18n
    ) {
        this.fileUploader.errorStream.pipe(forLifeOf(this)).subscribe((error) => {
            if (
                error.message === this.uploadWrongFileTypeErrorMessageI18N ||
                error.message === this.contentSizeErrorMessageI18N
            ) {
                this.uploadErrorMessage = error.message;
            } else {
                this.uploadErrorMessage = this._errorMessage;
            }
        });
    }

    private _renameFile(fileUploads: FileUpload[], fileName: string): File {
        const fileUpload = fileUploads[0];
        const file = fileUpload.file;
        const blob = file.slice(0, file.size, ContentTypes.Csv);
        const newFile = new File([blob], fileName, { type: ContentTypes.Csv });
        return newFile;
    }

    public async downloadUploadTemplate(): Promise<void> {
        const request = await this.getUploadTemplateRequest$.pipe(first()).toPromise();

        let templateDownloadUrl = '';
        if (this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)) {
            templateDownloadUrl = (await this._getBuyerUploadTemplateService.executeOnce(request))
                .buyerTemplateUrl;
        } else {
            templateDownloadUrl = (
                await this._getBuyerAccountUploadTemplateService.executeOnce(request)
            ).buyerAccountTemplateUrl;
        }

        const fileName = `${(await this.fileName$.pipe(first()).toPromise())
            .trim()
            .replace(/ /g, '_')}s_Template`;
        await this._downloadService.executeUrl(templateDownloadUrl, FileType.Csv, fileName);
    }

    public setFileSource(fileSourceElement: ElementRef): void {
        this.fileUpload$ = this.fileUploader
            .streamFileUploads(fileSourceElement.nativeElement as HTMLElement)
            .pipe(
                map(([fileUpload]) => fileUpload),
                shareReplay(1)
            );
    }

    public handleFileSelected(fileRef: string): void {
        this.hasFileSelected = !!fileRef;
        this.uploadErrorMessage = null;
    }

    private async _getPresignedUrl(): Promise<string> {
        const request = await this.getPresignedUrlRequest$.pipe(first()).toPromise();
        let generatePresignedUrlResponse = null;
        if (this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortal)) {
            generatePresignedUrlResponse = await this._generatePresignedBuyerUrlService.executeOnce(
                new GeneratePresignedUrlForBuyerRequest({ uploadType: request.uploadType })
            );
        } else {
            generatePresignedUrlResponse = await this._generatePresignedUrlService.executeOnce(
                request
            );
        }
        return generatePresignedUrlResponse.presignedUrl;
    }

    public setFileName(name: string): void {
        this.fileName$.next(name);
    }

    public setGetUploadTemplateRequest(request: GetBuyerAccountUploadTemplateRequest): void {
        this.getUploadTemplateRequest$.next(request);
    }

    public setGetPresignedUrlRequest(request: GeneratePresignedUrlForBuyerAccountRequest): void {
        this.getPresignedUrlRequest$.next(request);
    }
}
